import React from 'react';
import API_BASE_URL from '../config';
import { useSearch } from '../hooks/useSearch';
import DateFilter from './DateFilter';
import VideoGroupCard from './VideoGroupCard';

const MEDIUM_SCORE_MIN = 0.40; // must match backend RELEVANCE_SCORE_THRESHOLD

function SearchWidget({
  channelUrl: propChannelUrl,
  customerKey: propCustomerKey,
  signedToken = '',
  backendUrl = API_BASE_URL,
  theme = 'light',
  onSearchStart = () => {},
  onSearchEnd = () => {},
  onResultClick = () => {},
  showDemoSelector = false,
  className = '',
  style = {}
}) {
  const {
    queryResults, matchContexts, isLoading,
    lastQueryPhrase, lastChannelUrl,
    dateFilter, setDateFilter,
    demoConfigs, demoConfigsError, selectedDemo,
    channelUrl, customerKey, setChannelUrl, setCustomerKey,
    handleQuerySubmit, handleDemoSelection,
    formatTimestamp, getDisplayText, getActionProps,
  } = useSearch({
    backendUrl,
    propChannelUrl,
    propCustomerKey,
    signedToken,
    showDemoSelector,
    onSearchStart,
    onSearchEnd,
  });

  const isDark = theme === 'dark';
  const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

  const [collapsedGroups, setCollapsedGroups] = React.useState(new Set());
  const toggleCollapse = (idx) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });
  React.useEffect(() => { if (isLoading) setCollapsedGroups(new Set()); }, [isLoading]);
  React.useEffect(() => {
    if (queryResults.length > 0) {
      setCollapsedGroups(new Set(queryResults.map((_, i) => i)));
    }
  }, [queryResults]);

  const getMatchLabel = (score) => {
    if (score >= 0.65)            return { label: 'High',   color: '#10b981' };
    if (score >= MEDIUM_SCORE_MIN) return { label: 'Medium', color: '#f59e0b' };
    return                               { label: 'Low',    color: '#6b7280' };
  };

  const colors = isDark
    ? {
        bg: '#1a1a1a',
        text: '#e5e5e5',
        textMuted: '#999',
        card: '#242424',
        border: '#333',
        inputBg: '#2a2a2a',
        accent: '#6366f1',
        accentSoft: '#312e81',
        pillBg: '#2a2a2a',
        pillBorder: '#3a3a3a',
        highlight: '#4a4a4a',
        altRow: '#1f1f1f',
      }
    : {
        bg: '#ffffff',
        text: '#1a1a1a',
        textMuted: '#888',
        card: '#ffffff',
        border: '#eee',
        inputBg: '#fafafa',
        accent: '#111',
        accentSoft: '#f5f5f5',
        pillBg: '#f5f5f5',
        pillBorder: '#e5e5e5',
        highlight: '#fef9c3',
        altRow: '#fafafa',
      };

  const hasDemos = showDemoSelector && !demoConfigsError && Object.keys(demoConfigs).length > 0;
  const showCredentialInputs = !signedToken && (
    !propChannelUrl || !propCustomerKey
      ? (showDemoSelector ? (demoConfigsError || !selectedDemo) : true)
      : false
  );
  const emptyStateMessage = hasDemos
    ? 'Select a demo above and search to see Inquiros in action'
    : (showCredentialInputs
      ? 'Enter your channel URL and customer key, then search to see results'
      : 'Search to see results');

  return (
    <div className={`clipt-search-widget ${className}`} style={{
      fontFamily: fontStack,
      maxWidth: '780px',
      margin: '0 auto',
      color: colors.text,
      ...style
    }}>
      {/* Demo pills */}
      {hasDemos && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '28px' }}>
          {Object.entries(demoConfigs).map(([key, config]) => {
            const active = selectedDemo === key;
            return (
              <button
                key={key}
                onClick={() => handleDemoSelection(key)}
                style={{
                  padding: '8px 20px',
                  fontSize: '0.875rem',
                  fontFamily: fontStack,
                  fontWeight: active ? 600 : 450,
                  backgroundColor: active ? colors.accent : colors.pillBg,
                  color: active ? '#fff' : colors.text,
                  border: `1px solid ${active ? colors.accent : colors.pillBorder}`,
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  outline: 'none',
                }}
              >
                {config.name}
              </button>
            );
          })}
        </div>
      )}

      {showDemoSelector && demoConfigsError && (
        <div style={{
          marginBottom: '20px',
          textAlign: 'center',
          padding: '10px 16px',
          backgroundColor: '#fffbeb',
          borderRadius: '8px',
          border: '1px solid #fde68a',
          color: '#92400e',
          fontSize: '0.85rem',
        }}>
          Demo configs unavailable. Enter credentials below.
        </div>
      )}

      {/* Search form */}
      <form onSubmit={handleQuerySubmit} style={{ marginBottom: '32px' }}>
        <div style={{ position: 'relative', marginBottom: showCredentialInputs ? '12px' : '0' }}>
          <input
            name="queryPhrase"
            type="text"
            placeholder="Search across videos..."
            required
            style={{
              width: '100%',
              padding: '14px 16px',
              paddingRight: '110px',
              fontSize: '1rem',
              fontFamily: fontStack,
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              background: colors.inputBg,
              color: colors.text,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => { e.target.style.borderColor = isDark ? '#555' : '#ccc'; }}
            onBlur={(e) => { e.target.style.borderColor = colors.border; }}
          />
          <button
            type="submit"
            disabled={isLoading}
            style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              padding: '8px 20px',
              fontSize: '0.875rem',
              fontFamily: fontStack,
              fontWeight: 500,
              backgroundColor: colors.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'wait' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {showCredentialInputs && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              name="channelURL"
              type="text"
              placeholder="Channel URL"
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              required={!propChannelUrl && !selectedDemo}
              style={{
                flex: 1,
                padding: '10px 12px',
                fontSize: '0.85rem',
                fontFamily: fontStack,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.inputBg,
                color: colors.text,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <input
              name="customerKey"
              type="text"
              placeholder="Customer key"
              value={customerKey}
              onChange={(e) => setCustomerKey(e.target.value)}
              required={!propCustomerKey && !selectedDemo}
              style={{
                flex: 1,
                padding: '10px 12px',
                fontSize: '0.85rem',
                fontFamily: fontStack,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.inputBg,
                color: colors.text,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}
      </form>

      {/* Date filter */}
      <DateFilter value={dateFilter} onChange={setDateFilter} colors={colors} />

      {/* Results */}
      {queryResults.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {queryResults.map((videoGroup, groupIndex) => (
            <VideoGroupCard
              key={videoGroup.video_id}
              videoGroup={videoGroup}
              groupIndex={groupIndex}
              queryPhrase={lastQueryPhrase}
              matchContexts={matchContexts}
              isCollapsed={collapsedGroups.has(groupIndex)}
              onToggleCollapse={() => toggleCollapse(groupIndex)}
              customerKey={customerKey}
              channelUrl={lastChannelUrl || channelUrl}
              signedToken={signedToken}
              backendUrl={backendUrl}
              searchDone={!isLoading}
              isDark={isDark}
              colors={colors}
              fontStack={fontStack}
              formatTimestamp={formatTimestamp}
              getDisplayText={getDisplayText}
              getActionProps={getActionProps}
              onResultClick={onResultClick}
              getMatchLabel={getMatchLabel}
            />
          ))}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '56px 20px',
          color: colors.textMuted,
          fontSize: '0.95rem',
        }}>
          {emptyStateMessage}
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .clipt-search-widget input::placeholder {
            color: ${colors.textMuted};
          }
          .clipt-search-widget input:focus {
            border-color: ${isDark ? '#555' : '#ccc'} !important;
          }
        `}
      </style>
    </div>
  );
}

export default SearchWidget;
